import { Box, Card, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCalendar, IconCalendarDue, IconCreditCard, IconHourglass, IconRepeat } from "@tabler/icons-react";
import type { ChargeContract } from "../../../../apis/chargeContract";
import { MoneyCell } from "../../../../components/billing/MoneyCell";
import { formatDate } from "../../../../utils/format";

const freqLabel: Record<string, string> = { MONTHLY: "Сар бүр", QUARTERLY: "Улирал бүр", ANNUAL: "Жил бүр" };

interface Props {
  contract: ChargeContract;
  /** Best-effort estimated next-invoice amount (sum of recurring components). */
  nextEstimate: number;
}

// The contract type does not carry explicit billingTerms / nextInvoice; we
// derive them from the recurring component's anchor day and frequency.
export function BillingTerms({ contract, nextEstimate }: Props) {
  const recurring = (contract.components ?? []).find((c) => c.componentType === "RECURRING_SUBSCRIPTION");
  const frequency = recurring?.recurringFrequency ?? "MONTHLY";
  const anchorDay = recurring?.recurringAnchorDay ?? 1;

  // Next anchor date: the next occurrence of anchorDay from today.
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), anchorDay);
  if (next <= now) next.setMonth(next.getMonth() + 1);
  const nextDate = next.toISOString();

  const rows: { icon: React.ReactNode; label: string; value: React.ReactNode; accent?: boolean }[] = [
    { icon: <IconRepeat size={18} />, label: "Нэхэмжлэх давтамж", value: freqLabel[frequency] ?? frequency },
    { icon: <IconCalendar size={18} />, label: "Нэхэмжлэх гарах өдөр", value: `Сарын ${anchorDay}-нд` },
    { icon: <IconHourglass size={18} />, label: "Төлбөрийн хугацаа", value: "14 хоног" },
    { icon: <IconCreditCard size={18} />, label: "Төлбөрийн арга", value: "Дансаар / QPay" },
    {
      icon: <IconCalendarDue size={18} />,
      label: "Дараагийн нэхэмжлэх",
      accent: true,
      value: (
        <Group gap={6} wrap="nowrap" align="baseline">
          <Text size="sm" fw={600}>{formatDate(nextDate)}</Text>
          <Text size="sm" c="dimmed">·</Text>
          <Group gap={2} wrap="nowrap" align="baseline">
            <Text size="sm" c="dimmed">~</Text>
            <MoneyCell value={nextEstimate} currency={contract.currency} fw={700} />
          </Group>
        </Group>
      )
    }
  ];

  return (
    <Card withBorder radius="md" padding="lg" style={{ position: "sticky", top: 16 }}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="md" style={{ letterSpacing: "0.06em" }}>
        Төлбөрийн нөхцөл
      </Text>
      <Stack gap="md">
        {rows.map((r) => (
          <Box
            key={r.label}
            p={r.accent ? "sm" : 0}
            style={
              r.accent
                ? {
                    borderRadius: "var(--mantine-radius-md)",
                    background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))"
                  }
                : undefined
            }
          >
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color={r.accent ? "brand" : "gray"} size={32} radius="md">
                {r.icon}
              </ThemeIcon>
              <Box>
                <Text size="xs" c="dimmed">{r.label}</Text>
                <Box mt={2}>
                  {typeof r.value === "string" ? <Text size="sm" fw={600}>{r.value}</Text> : r.value}
                </Box>
              </Box>
            </Group>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
